import { CalendarIcon, ChevronsDown, CircleX } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useMemo, useState } from "react";

import DatePicker from "@/components/ui/date-picker";
import { MultiSelect } from "@/components/ui/multi-select";
import { Badge } from "@/components/ui/badge";

import {
  PursuitCreator,
  PursuitKanban,
} from "@/features/pursuit/PursuitKanban";
import { useGetUserPursuits } from "@/features/pursuit/useGetUserPursuits";
import { useUiStore } from "@/stores/UiStore";
import KanbanFilters from "@/features/pursuit/KanbanFilters";
import { PursuitCalendar } from "@/features/pursuit/PursuitCalendar";
import { ShowOnceJourneyDialog } from "@/features/changelog/ShowOnceJourneyDialog";

export const statusColumns = [
  { id: "assessment", title: "Assessment" },
  { id: "in_progress", title: "In Progress" },
  { id: "submitted", title: "Submitted" },
  { id: "won", title: "Won" },
];

const pursuitType = [
  {
    value: "federal",
    label: (
      <Badge variant="secondary" className="rounded-md">
        <span className="truncate">Federal</span>
      </Badge>
    ),
  },
  {
    value: "sled",
    label: (
      <Badge variant="secondary" className="rounded-md">
        <span className="truncate">SLED</span>
      </Badge>
    ),
  },
  {
    value: "grant",
    label: (
      <Badge variant="secondary" className="rounded-md">
        <span className="truncate">Grant</span>
      </Badge>
    ),
  },
  {
    value: "sbir",
    label: (
      <Badge variant="secondary" className="rounded-md">
        <span className="truncate">SBIR</span>
      </Badge>
    ),
  },
  {
    value: "upload",
    label: (
      <Badge variant="secondary" className="rounded-md">
        <span className="truncate">Upload</span>
      </Badge>
    ),
  },
];

export default function Pursuit() {
  const { data, isLoading } = useGetUserPursuits();
  const [activeFilters, setActiveFilters] = useState({ type: [], date: null });
  const navigate = useNavigate();
  const togglePursuitCalendarView = useUiStore(
    (state) => state.togglePursuitCalendarView
  );
  const isPursuitCalendarView = useUiStore(
    (state) => state.pursuitCalendarView
  );

  const filteredData = useMemo(() => {
    if (!data) return [];

    return data.filter((pursuit) => {
      const matchesType =
        activeFilters.type.length === 0 ||
        activeFilters.type.includes(pursuit.search_type);
      const matchesDate =
        !activeFilters.date ||
        new Date(pursuit.due_date) <= new Date(activeFilters.date);

      return matchesType && matchesDate;
    });
  }, [data, activeFilters]);
  return (
    <div className="flex flex-col gap-4 container">
      <KanbanFilters
        calendarSwitched={isPursuitCalendarView}
        onCalendarSwitchClicked={togglePursuitCalendarView}
        renderNewButton={() => <PursuitCreator />}
        renderFilters={() => (
          <>
            <MultiSelect
              options={pursuitType}
              onValueChange={(values) => {
                setActiveFilters({ ...activeFilters, type: values });
              }}
              placeholder={
                <span className="flex items-center hover:text-foreground gap-1">
                  <ChevronsDown className="size-3.5" /> Type
                </span>
              }
              maxCount={1}
              className="border-x border-y-0 rounded-none min-h-8 hover:bg-muted h-full"
            />
            <div
              className="hover:bg-muted"
              style={{
                backgroundColor: activeFilters.date
                  ? "hsl(var(--secondary))"
                  : undefined,
              }}
            >
              <DatePicker
                selected={activeFilters.date}
                onSelect={(date) => {
                  setActiveFilters({ ...activeFilters, date });
                }}
                onClick={() =>
                  activeFilters.date &&
                  setActiveFilters({ ...activeFilters, date: null })
                }
                icon={activeFilters.date ? CircleX : CalendarIcon}
                placeholder="Due date"
                className="border-none bg-transparent w-full"
              />
            </div>
          </>
        )}
      />

      {isPursuitCalendarView ? (
        <div className="p-4">
          <PursuitCalendar
            events={filteredData.map((pursuit) => ({
              day: new Date(pursuit.due_date),
              events: [
                {
                  id: pursuit.id,
                  name: pursuit.title,
                  onClick: () => navigate(`./${pursuit.id}`),
                },
              ],
            }))}
          />
        </div>
      ) : (
        <PursuitKanban
          className="bg-background rounded-md border"
          data={filteredData}
          isLoading={isLoading}
        />
      )}
      <ShowOnceJourneyDialog />
    </div>
  );
}
